import { TokenWithBalance, TokenWithValue } from '@/domain/common/types'
import { SwapFormSchema } from './validators'
import { UseFormReturn } from 'react-hook-form'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { MarketInfo } from '@/domain/market-info/marketInfo'
import { PageStatus } from '@/features/dialogs/common/types'
import { Address } from 'viem'
import { Token } from '@/domain/types/Token'

export interface SwapFormNormalizedData {
  from: TokenWithValue
  to: TokenWithValue
}

export type SwapMode = 'swap' | 'wrap' | 'unwrap'

export interface UseDebouncedFormValuesArgs {
  form: UseFormReturn<SwapFormSchema>
  swappableAssets: TokenWithBalance[]
}

export interface UseDebouncedDialogFormValuesResult {
  debouncedFormValues: SwapFormNormalizedData
  isFormValid: boolean
  isDebouncing: boolean
}

export interface SwapPairConfig {
  from: TokenSymbol | string
  to: TokenSymbol | string
}

export interface GetPriceResult {
  statusCode: number
  result: {
    inputToken: Address
    outputToken: Address
    feeToken: Address
    outputReceiver: Address
    inputAmount: string
    outputAmount: string
    partnerFee: string
    routingFee: string
    effectiveOutputAmount: string
    minOutputAmount: string
    liquidityModules: string[]
    router: Address
    surgeProtectionApplied?: string
    surgeValue?: number
    inputAmountUSD?: string | null
    outputAmountUSD?: string | null
    effectiveInputAmount?: string | null
    effectiveOutputAmountUSD?: string | null
  }
}

export interface GetPriceError {
  statusCode: number
  error: string
}

export interface UseSwapResults {
  form: UseFormReturn<SwapFormSchema>
  pageStatus: PageStatus & { resetForm: () => void }
  guestMode: boolean
  priceData: GetPriceResult | GetPriceError | undefined | null
  priceUpdating: boolean
  fromTokenField: UseSwapFormFieldsResults
  toTokenField: UseSwapFormFieldsResults
  fromToken: TokenWithValue
  toToken: TokenWithValue
  swapMode: SwapMode
  swapError: boolean
  swapState: string
  quoteError: boolean
  slippage: Percentage
  surgeAcknowledged: boolean
  setSurgeAcknowledged: (acknowledged: boolean) => void
  setSlippage: (slippage: Percentage) => void
  openConnectModal: () => void
  swapTokens: () => void
  swap: () => void
  wrapUnwrap: () => void
}

export interface UseSwapFormFieldsArgs {
  form: UseFormReturn<SwapFormSchema>
  assets: TokenWithBalance[]
  fieldName: 'from' | 'to'
  counterpartFieldName: 'to' | 'from'
  marketInfo: MarketInfo
  isOnSwapPage: boolean
  filterZeroBalance: boolean
  toTokens?: Token[]
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  resetErrors: () => void
  setSurgeAcknowledged: (acknowledged: boolean) => void
}

export interface UseSwapFormFieldsResults {
  selectedAsset: TokenWithBalance
  availableAssets: TokenWithBalance[]
  changeToken: (newSymbol: TokenSymbol) => void
  maxSelectedFieldName: string | undefined
  maxValue: NormalizedUnitNumber
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onSetMax: ((max: NormalizedUnitNumber) => void) | undefined
}

export class SwapError extends Error {
  public details?: Record<string, unknown>

  constructor(message: string, details?: Record<string, unknown>) {
    super(message)
    this.name = 'SwapError'
    this.details = details
  }
}

export class MissingQuoteError extends SwapError {}

export interface SwapViewProps {
  withHeading?: boolean
  swap: UseSwapResults
  simplified?: boolean
  toTokens?: Token[]
  featuredToAssets?: TokenSymbol[]
}
